import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyInput, moneyInputValue, parseMoney } from "@/components/MoneyInput";
import type { Account } from "@/lib/finance";

export const EMPTY_ASSET = { opened_on: new Date().toISOString().slice(0, 10), purchase_value: "", purchase_date: "", linked_loan_id: "none", paying: "", depreciation_on: "auto", depreciation_rate: "15" };
export const NEW_LINK = "__new";
type AssetState = typeof EMPTY_ASSET & { subtype: string };

export const assetFromAccount = (a: Account) => ({
  opened_on: "", purchase_value: moneyInputValue(a.purchase_value ?? null), purchase_date: a.purchase_date ?? "",
  linked_loan_id: a.linked_loan_id ?? "none", paying: a.linked_loan_id ? "yes" : "no", depreciation_on: a.depreciation_on ? "on" : "off",
  depreciation_rate: a.depreciation_rate ? String(Math.round(a.depreciation_rate * 1000) / 10) : "15",
});

export const assetPayload = (f: AssetState, isNew: boolean) => {
  const fixed = f.subtype === "house" || f.subtype === "land";
  const on = !fixed && (f.depreciation_on === "auto" ? f.subtype === "vehicle" : f.depreciation_on === "on");
  const rate = Number(f.depreciation_rate.replace(",", "."));
  return {
    ...(isNew ? { opened_on: f.opened_on || null } : {}),
    purchase_value: f.purchase_value.trim() ? parseMoney(f.purchase_value) : null,
    purchase_date: f.purchase_date || null,
    linked_loan_id: !f.linked_loan_id || f.linked_loan_id === "none" || (isNew && f.paying !== "yes") ? null : f.linked_loan_id,
    depreciation_on: on, depreciation_rate: on && rate > 0 && rate < 100 ? rate / 100 : null,
  };
};

function DateField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
      <Input id={id} type="date" value={value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)} className="h-11" data-testid={`account-form-${id}`} />
    </div>
  );
}

function YesNo({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5" data-testid="asset-paying">
      <Label className="text-sm text-slate-700">¿Todavía lo estás pagando con un crédito?</Label>
      <div className="grid grid-cols-2 gap-2">
        {([["yes", "Sí"], ["no", "No"]] as const).map(([v, l]) => (
          <button key={v} type="button" onClick={() => onChange(v)} data-testid={`asset-paying-${v}`}
            className={`h-11 rounded-md border text-sm font-semibold transition-colors ${value === v ? "border-[#1B2A4A] bg-[#1B2A4A] text-white" : "border-[#DDE1E8] bg-white text-[#1B2A4A]"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export function AssetFields({ f, set, isNew, loans, onCreateLoan }: { f: AssetState; set: (k: string, v: string) => void; isNew: boolean; loans: Account[]; onCreateLoan?: () => void }) {
  const fixed = f.subtype === "house" || f.subtype === "land";
  const on = !fixed && (f.depreciation_on === "auto" ? f.subtype === "vehicle" : f.depreciation_on === "on");
  const showLoan = !isNew || f.paying === "yes";
  return (
    <div className="space-y-4 rounded-xl border border-[#ECE3D3] bg-[#FAF7F1] p-4" data-testid="asset-fields">
      {isNew && <DateField id="opened_on" label="Fecha de alta (de cuándo es ese valor)" value={f.opened_on} onChange={(v) => set("opened_on", v)} />}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="purchase_value" className="text-sm text-slate-700">Valor de compra (opcional)</Label>
          <MoneyInput id="purchase_value" value={f.purchase_value} onChange={(v) => set("purchase_value", v)} className="h-11" testId="account-form-purchase_value" />
        </div>
        <DateField id="purchase_date" label="Fecha de compra (opcional)" value={f.purchase_date} onChange={(v) => set("purchase_date", v)} />
      </div>
      {isNew && <YesNo value={f.paying} onChange={(v) => set("paying", v)} />}
      {showLoan && (
      <div className="space-y-1.5">
        <Label className="text-sm text-slate-700">Crédito ligado{isNew ? "" : " (opcional)"}</Label>
        <Select value={f.linked_loan_id} onValueChange={(v: string) => { if (!v) return; if (v === NEW_LINK) onCreateLoan?.(); else set("linked_loan_id", v); }}>
          <SelectTrigger className="h-11" data-testid="account-form-linked-loan"><SelectValue>{loans.find((l) => l.id === f.linked_loan_id)?.name ?? "Sin crédito"}</SelectValue></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="none">Sin crédito</SelectItem>
            {loans.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            {onCreateLoan && <SelectItem value={NEW_LINK} className="font-semibold text-[#2C4F91]" data-testid="linked-loan-create">+ Crear este crédito</SelectItem>}
          </SelectContent>
        </Select>
      </div>)}
      {fixed ? <p className="text-xs text-slate-500" data-testid="asset-fixed-note">Las casas, departamentos y terrenos no cambian de valor solos. Usa “Actualizar valor” cuando quieras cambiarlo.</p> : (
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#1B2A4A]">Pierde valor cada mes</p>
          <p className="text-xs text-slate-500">Se aplica al cierre de cada mes. Para autos viene encendido.</p>
        </div>
        <Switch checked={on} onCheckedChange={(c: boolean) => set("depreciation_on", c ? "on" : "off")} data-testid="account-form-depreciation" />
      </div>)}
      {on && (
        <div className="space-y-1.5">
          <Label htmlFor="depreciation_rate" className="text-sm text-slate-700">Depreciación anual (%)</Label>
          <Input id="depreciation_rate" inputMode="decimal" value={f.depreciation_rate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("depreciation_rate", e.target.value.replace(/[^\d.,]/g, ""))} className="h-11" data-testid="account-form-depreciation-rate" />
        </div>
      )}
    </div>
  );
}
